/**
 * Handles an `unhandledrejection` event that may indicate a stale Vite chunk
 * after a new deployment.
 *
 * Three failure shapes are recognised:
 *  1. Dynamic-import failures in modern browsers:
 *       reason.message includes "Failed to fetch dynamically imported module"
 *  2. Dynamic-import failures on Safari / older browsers:
 *       reason.message includes "Importing a module script failed"
 *  3. <script type="text/javascript"> tag load failure (e.g. a 404 on a hashed
 *     script URL after a deploy). The browser rejects with a DOM ErrorEvent
 *     — fingerprinted by Sentry as "Event `Event` (type=error) captured as
 *     promise rejection" — whose .message is empty, so the string checks above
 *     do not match it. (DEVAMS-1942 / Sentry FRONTEND-96)
 *
 * When any condition matches, the event is suppressed (preventDefault) and
 * `reloadFn` is called so the page reloads to pick up the fresh bundle.
 *
 * @param event   The PromiseRejectionEvent fired on the window.
 * @param reloadFn  Side-effectful reload callback (defaults to reloadOnStaleChunk).
 * @returns `true` when the event was handled (and reloadFn was called).
 */
export function handleUnhandledRejection(
  event: PromiseRejectionEvent,
  reloadFn: () => void,
): boolean {
  const reason: unknown = event.reason

  const isStaleChunk =
    (typeof reason === 'object' &&
      reason !== null &&
      'message' in reason &&
      typeof (reason as { message: unknown }).message === 'string' &&
      (
        (reason as { message: string }).message.includes('Failed to fetch dynamically imported module') ||
        (reason as { message: string }).message.includes('Importing a module script failed')
      )
    ) ||
    // Script tag load failure after a new deploy: the browser rejects with a
    // DOM ErrorEvent (no .message) rather than a standard Error — fingerprinted
    // by Sentry as "Event `Event` (type=error) captured as promise rejection"
    // (DEVAMS-1942 / Sentry FRONTEND-96).
    (reason instanceof Event && reason.type === 'error' && reason.target instanceof HTMLScriptElement)

  if (isStaleChunk) {
    event.preventDefault()
    reloadFn()
    return true
  }

  return false
}
